import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { toast } from 'components/global/CUK/Toast/Toast'

import {
  OPERATION_SITE_SET_VISIBILITY_IN_CENTER,
  OPERATION_SITE_SYNC_PROJECT_FIELDS,
  OPERATION_SITE_UPDATE_VISIBILITY_BY_IDS,
  OPERATION_SITE_UPDATE_VISIBILITY_BY_QUERY
} from './operations-gql/mutations'
import useFilters, { toSitesUnscopeVariables, useSelection } from './store'
import { SITES_LIST_QUERIES } from './constants'
import { VisibilityInCenter } from './types'

const REFETCH_QUERIES = ['FindSiteUnscoped']

type BulkUpdateData = {
  count: number | null
}

export const useSetVisibilityInCenter = (id?: string) => {
  const { t } = useTranslation()
  const [mutate, { loading }] = useMutation(OPERATION_SITE_SET_VISIBILITY_IN_CENTER, {
    refetchQueries: REFETCH_QUERIES
  })

  const setVisibility = async (visibility: VisibilityInCenter) => {
    if (!id) return

    try {
      await mutate({ variables: { id, visibility } })
      toast.success(t('admin_sites.card.visibility_success'))
    } catch {
      toast.error(t('admin_sites.card.visibility_error'))
    }
  }

  return { setVisibility, loading }
}

export const useBulkSetVisibilityInCenter = () => {
  const { t } = useTranslation()
  const [filters] = useFilters()
  const { isAllSelected, selectedIds, clear } = useSelection()
  const [updateByIds, { loading: loadingByIds }] = useMutation<BulkUpdateData>(
    OPERATION_SITE_UPDATE_VISIBILITY_BY_IDS,
    { refetchQueries: SITES_LIST_QUERIES }
  )
  const [updateByQuery, { loading: loadingByQuery }] = useMutation<BulkUpdateData>(
    OPERATION_SITE_UPDATE_VISIBILITY_BY_QUERY,
    { refetchQueries: SITES_LIST_QUERIES }
  )

  const updateSelection = (visibility: VisibilityInCenter) => {
    if (isAllSelected) {
      return updateByQuery({ variables: { ...toSitesUnscopeVariables(filters), visibility } })
    }
    return updateByIds({ variables: { ids: selectedIds, visibility } })
  }

  const setVisibility = async (visibility: VisibilityInCenter) => {
    try {
      const { data } = await updateSelection(visibility)
      toast.success(t('admin_sites.selection.visibility_success', { count: data?.count ?? 0 }))
      clear()
    } catch {
      toast.error(t('admin_sites.selection.visibility_error'))
    }
  }

  return { setVisibility, loading: loadingByIds || loadingByQuery }
}

export const useSyncProjectFields = (id?: string) => {
  const { t } = useTranslation()
  const [mutate, { loading }] = useMutation(OPERATION_SITE_SYNC_PROJECT_FIELDS, {
    refetchQueries: REFETCH_QUERIES
  })

  const sync = async () => {
    if (!id) return

    try {
      await mutate({ variables: { id } })
      toast.success(t('admin_sites.card.sync_success'))
    } catch {
      toast.error(t('admin_sites.card.sync_error'))
    }
  }

  return { sync, loading }
}
